import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import './App.css';
import { 
  FaArrowRight, 
  FaLinkedin, 
  FaEnvelope, 
  FaCamera, 
  FaUsers, 
  FaDatabase, 
  FaMobile 
} from 'react-icons/fa';

// Firebase
import { db } from './firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

// EmailJS
import emailjs from 'emailjs-com';

function App() {
  // State to track mobile menu open/close
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // State to control visibility of "Back to Top" button
  const [showBackToTop, setShowBackToTop] = useState(false);
  
  // State to track if header is scrolled
  const [isHeaderScrolled, setIsHeaderScrolled] = useState(false);
  
  // State to track scroll direction for header animation
  const [lastScrollY, setLastScrollY] = useState(0);
  const [headerVisible, setHeaderVisible] = useState(true);
  
  // State for form loading
  const [isLoading, setIsLoading] = useState(false);
  
  // Ref for sections to enable scroll animations
  const featuresRef = useRef(null);
  const originRef = useRef(null);
  const contactRef = useRef(null);
  
  // Particle animation
  const [particles, setParticles] = useState([]);
  
  // Typing animation state
  const [displayText, setDisplayText] = useState('');
  const fullText = 'Simplifying School Pickups, One Tap at a Time.';

  // Contact form states
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [formStatus, setFormStatus] = useState(null); // 'success', 'error', or null
  
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1,
        delayChildren: 0.3,
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { 
      y: 0, 
      opacity: 1,
      transition: { type: "spring", stiffness: 100 }
    }
  };
  
  // Particle effect for hero section
  useEffect(() => {
    // Create particles on mount
    const createParticles = () => {
      const particlesArray = [];
      
      for (let i = 0; i < 50; i++) {
        particlesArray.push({
          id: i,
          x: Math.random() * window.innerWidth,
          y: Math.random() * window.innerHeight,
          size: Math.random() * 2 + 1,
          speedX: (Math.random() - 0.5) * 0.5,
          speedY: (Math.random() - 0.5) * 0.5,
          opacity: Math.random() * 0.5 + 0.2,
        });
      }
      
      setParticles(particlesArray);
    };
    
    createParticles();
    
    // Handle window resize
    const handleResize = () => {
      createParticles();
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Text typing animation
  useEffect(() => {
    if (displayText.length < fullText.length) {
      const timeout = setTimeout(() => {
        setDisplayText(fullText.substring(0, displayText.length + 1));
      }, 50);
      
      return () => clearTimeout(timeout);
    }
  }, [displayText, fullText]);
  
  // Parallax effect for hero section
  const [parallaxY, setParallaxY] = useState(0);
  
  useEffect(() => {
    const handleScroll = () => {
      setParallaxY(window.scrollY * 0.3);
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Product components data
  const productSlides = [
    {
      title: 'Teacher Dashboard & Camera System',
      description: 'Teachers can manually enter license plates or use connected AI-powered cameras for automated detection. Computer vision technology provides accurate identification while maintaining security standards.',
      image: '/team.png',
    },
    {
      title: 'Admin Control Center',
      description: 'Comprehensive dashboard for managing teachers, devices, guardians, students, and guests. Admins control tag distribution, add temporary guests, and monitor system settings with real-time capabilities.',
      image: '/vision.png',
    },
    {
      title: 'Secure Database System',
      description: 'Robust backend infrastructure with secure data storage and real-time synchronization. Manages all records and authentication while ensuring FERPA compliance and data protection.',
      image: '/journey.png',
    },
    {
      title: 'Guardian Mobile App',
      description: 'Mobile app for guardians to track pickup status and receive real-time notifications. Multiple communication channels keep families informed throughout the pickup process.',
      image: '/team.png',
    },
  ];

  // Handle intersection observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animated');
        }
      });
    }, { threshold: 0.1 });
    
    // Observe all sections that should animate on scroll
    const sections = document.querySelectorAll('.animate-on-scroll');
    sections.forEach(section => {
      observer.observe(section);
    });
    
    return () => {
      sections.forEach(section => {
        observer.unobserve(section);
      });
    };
  }, []);

  // Handle contact form submission
  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('submitting');
    setIsLoading(true);

    // Form validation
    if (!name || !email || !message) {
      setFormStatus('error');
      setIsLoading(false);
      return;
    }

    // Add a slight delay to show loading animation
    await new Promise(resolve => setTimeout(resolve, 1000));

    // 1. Store form data in Firestore
    try {
      await addDoc(collection(db, 'contacts'), {
        name,
        email,
        message,
        timestamp: serverTimestamp(),
      });
    } catch (error) {
      console.error('Error saving to Firestore:', error);
      setFormStatus('error');
      setIsLoading(false);
      return;
    }

    // 2. Send an email notification via EmailJS
    const templateParams = {
      from_name: name,
      from_email: email,
      message: message,
    };

    try {
      // Replace with your EmailJS details
      const result = await emailjs.send(
        'service_mjr74fn',
        'template_ew9ge7d',
        templateParams,
        'V0J2BJXMfcZKm5k5_'
      );
      console.log('Email sent successfully:', result.text);
      setFormStatus('success');
      setIsLoading(false);
      
      // Reset the form
      setName('');
      setEmail('');
      setMessage('');
      
      // Reset form status after 5 seconds
      setTimeout(() => {
        setFormStatus(null);
      }, 5000);
    } catch (err) {
      console.error('Email send error:', err);
      setFormStatus('error');
      setIsLoading(false);
    }
  };

  // Show "Back to Top" button when scrolling below a certain point
  // and change header style with enhanced scroll behavior
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      // Show back to top button when scrolled down past hero section
      setShowBackToTop(currentScrollY > 200);
      
      // Add scrolled class to header
      setIsHeaderScrolled(currentScrollY > 50);
      
      // Header visibility based on scroll direction
      // Only hide/show header if we're past the hero section
      if (currentScrollY > 150) {
        if (currentScrollY > lastScrollY) {
          // Scrolling down - hide header
          setHeaderVisible(false);
        } else {
          // Scrolling up - show header
          setHeaderVisible(true);
        }
      } else {
        // Always show header when near the top
        setHeaderVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };
    
    // Add passive option for better scroll performance
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Close mobile menu when clicking a link
  const handleNavLinkClick = () => {
    setIsMenuOpen(false);
  };

  // Scroll smoothly to top
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Scroll to specific section
  const scrollToSection = (sectionRef) => {
    if (sectionRef && sectionRef.current) {
      sectionRef.current.scrollIntoView({ behavior: 'smooth' });
      handleNavLinkClick();
    }
  };

  // Handle URL hash navigation (for direct links like /#contact)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#contact' && contactRef.current) {
      // Delay to ensure the page is loaded
      setTimeout(() => {
        contactRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    } else if (hash === '#origin' && originRef.current) {
      setTimeout(() => {
        originRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 500);
    }
  }, []);

  return (
    <div className="app-container">
      {/* Header */}
      <header className={`header ${isHeaderScrolled ? 'scrolled' : ''} ${headerVisible ? 'visible' : 'hidden'}`}>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <motion.div
            className="logo"
            onClick={scrollToTop}
            whileHover={{ scale: 1.1 }}
            transition={{ type: 'spring', stiffness: 200 }}
          >
            SafeLine Pickup
          </motion.div>
        </Link>

        {/* Navigation links */}
        <nav className={isMenuOpen ? 'nav-mobile-open' : ''}>
          <ul className="nav-links">
            <li>
              <a href="#origin" onClick={() => scrollToSection(originRef)}>Product</a>
            </li>
            <li>
              <Link to="/team">Team</Link>
            </li>
            <li>
              <a href="#contact" onClick={() => scrollToSection(contactRef)}>
                Request a Demo
              </a>
            </li>
          </ul>
          
          {/* Login Button - appears in mobile menu only */}
          <motion.div
            className="header-login-container mobile-only"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Link to="/login" className="login-button-header" onClick={handleNavLinkClick}>
              <span>Login</span>
            </Link>
          </motion.div>
        </nav>

        {/* Hamburger icon for mobile */}
        <div
          className={`hamburger ${isMenuOpen ? 'open' : ''}`}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          <span />
          <span />
          <span />
        </div>

        {/* Login Button - desktop only */}
        <motion.div
          className="header-login-container desktop-only"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Link to="/login" className="login-button-header">
            <span>Login</span>
          </Link>
        </motion.div>
      </header>

      {/* Hero Section with Particles */}
      <motion.section
        className="hero-section"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        {/* Particle background */}
        <div className="particles-container">
          {particles.map((particle) => (
            <motion.div
              key={particle.id}
              className="particle"
              initial={{ 
                x: particle.x, 
                y: particle.y,
                opacity: particle.opacity
              }}
              animate={{
                x: particle.x + particle.speedX * 100,
                y: particle.y + particle.speedY * 100,
                opacity: [particle.opacity, particle.opacity * 0.5, particle.opacity],
              }}
              transition={{
                duration: 10,
                repeat: Infinity,
                repeatType: "reverse",
              }}
              style={{
                width: `${particle.size}px`,
                height: `${particle.size}px`,
                background: `rgba(255, 87, 34, ${particle.opacity})`,
                position: 'absolute',
                borderRadius: '50%',
              }}
            />
          ))}
        </div>
        
        {/* Parallax effect on title */}
        <motion.h1
          className="hero-title"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
          style={{ y: -parallaxY * 0.2 }}
        >
          Welcome to SafeLine Pickup
        </motion.h1>
        
        {/* Typing effect for subtitle */}
        <motion.p
          className="hero-subtitle"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2, type: "spring", stiffness: 100 }}
          style={{ y: -parallaxY * 0.1 }}
        >
          {displayText}<span className="cursor-blink">|</span>
        </motion.p>
        
        <motion.div
          className="hero-buttons"
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4, type: "spring", stiffness: 100 }}
        >
          <motion.button
            className="hero-button primary"
            whileHover={{ scale: 1.05, boxShadow: "0 10px 25px rgba(255, 87, 34, 0.4)" }}
            whileTap={{ scale: 0.95 }}
            onClick={() => scrollToSection(originRef)}
          >
            Discover More
          </motion.button>
          
          <motion.button
            className="hero-button secondary"
            whileHover={{ scale: 1.05, boxShadow: "0 10px 25px rgba(255, 87, 34, 0.2)" }}
            whileTap={{ scale: 0.95 }}
            onClick={() => scrollToSection(contactRef)}
          >
            Contact Us
          </motion.button>
        </motion.div>
      </motion.section>


      {/* Product Components Section */}
      <section id="origin" className="product-section animate-on-scroll" ref={originRef}>
        <div className="product-container">
          {/* Header */}
          <motion.div
            className="product-header"
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
        >
            <h2>Revolutionizing School Pickup Systems</h2>
            <p>
              SafeLine offers flexible pickup solutions with teacher dashboards for manual entry or automated camera systems, 
              admin controls for managing users and devices, optional guardian tag systems, and mobile app tracking for guardians.
            </p>
            <div className="coming-soon-badge">
              <span>🚀 Coming Soon to Texas Schools</span>
            </div>
          </motion.div>

          {/* Key Features Overview */}
        <motion.div
            className="features-overview"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
            <div className="feature-stat">
              <div className="stat-number">95%</div>
              <div className="stat-label">Faster Pickup Times</div>
            </div>
            <div className="feature-stat">
              <div className="stat-number">100%</div>
              <div className="stat-label">Automated Recognition</div>
            </div>
            <div className="feature-stat">
              <div className="stat-number">100%</div>
              <div className="stat-label">Live Analytics</div>
            </div>
            <div className="feature-stat">
              <div className="stat-number">100%</div>
              <div className="stat-label">Customization</div>
            </div>
          </motion.div>

          {/* Core Components Grid */}
          <motion.div
            className="components-grid"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            {productSlides.map((component, index) => (
              <motion.div
                key={index}
                className="component-card"
                variants={itemVariants}
                whileHover={{ y: -8, boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)" }}
              >
                <div className="component-icon">
                  {index === 0 && <FaCamera />}
                  {index === 1 && <FaUsers />}
                  {index === 2 && <FaDatabase />}
                  {index === 3 && <FaMobile />}
                </div>
                <div className="component-content">
                  <h3>{component.title}</h3>
                  <p>{component.description}</p>
                  <div className="component-tech">
                    {index === 0 && (
                      <>
                        <span className="tech-tag">Manual Entry</span>
                        <span className="tech-tag">AI Detection</span>
                        <span className="tech-tag">Computer Vision</span>
                      </>
                    )}
                    {index === 1 && (
                      <>
                        <span className="tech-tag">User Management</span>
                        <span className="tech-tag">Tag Distribution</span>
                        <span className="tech-tag">Live Monitoring</span>
                      </>
                    )}
                    {index === 2 && (
                      <>
                        <span className="tech-tag">Secure Database</span>
                        <span className="tech-tag">FERPA Compliant</span>
                        <span className="tech-tag">Real-time Sync</span>
                      </>
                    )}
                    {index === 3 && (
                      <>
                        <span className="tech-tag">Mobile Tracking</span>
                        <span className="tech-tag">Push Notifications</span>
                        <span className="tech-tag">Real-time Updates</span>
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Integration Flow */}
          <motion.div
            className="integration-section"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <h3>How It Works</h3>
            <div className="integration-flow">
              <div className="flow-step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h4>Guardian Arrival</h4>
                  <p>Teachers manually enter license plates, scan tags, or use automated camera detection</p>
                </div>
              </div>
              <div className="flow-arrow">→</div>
              <div className="flow-step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h4>System Verification</h4>
                  <p>Database matches entry with authorized guardian and student records</p>
                </div>
              </div>
              <div className="flow-arrow">→</div>
              <div className="flow-step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h4>Real-time Updates</h4>
                  <p>Mobile app notifies guardians and admin dashboard tracks all activity</p>
                </div>
              </div>
              <div className="flow-arrow">→</div>
              <div className="flow-step">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h4>Safe Pickup</h4>
                  <p>Students are efficiently delivered to verified guardians with full tracking</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Security & Compliance */}
          <motion.div
            className="security-section"
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            <div className="security-content">
              <div className="security-text">
                <h3>Enterprise-Grade Security</h3>
                  <p>
                    SafeLine prioritizes student safety and data protection with military-grade encryption, 
                    FERPA compliance, and secure cloud infrastructure. Our system enhances manual verification 
                    of guardians and guests through intelligent assistance, providing comprehensive oversight 
                    while maintaining complete privacy and security.
                  </p>
                <div className="security-features">
                  <div className="security-feature">
                    <span className="security-icon">🔒</span>
                    <span>256-bit Encryption</span>
                  </div>
                  <div className="security-feature">
                    <span className="security-icon">✅</span>
                    <span>FERPA Compliant</span>
                  </div>
                  <div className="security-feature">
                    <span className="security-icon">🛡️</span>
                    <span>SOC 2 Type II</span>
                  </div>
                  <div className="security-feature">
                    <span className="security-icon">🔐</span>
                    <span>Zero Trust Architecture</span>
                  </div>
                </div>
              </div>

            </div>
        </motion.div>
        </div>
      </section>


      {/* Contact Section */}
      <section id="contact" className="contact-section animate-on-scroll" ref={contactRef}>
        <motion.h2
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8 }}
        >
          Request a Demo
        </motion.h2>
        
        <motion.p
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, delay: 0.1 }}
        >
          Experience SafeLine in action with a personalized demo. 
          See how our system can transform your school's pickup process.
        </motion.p>
        
        <motion.form
          className="contact-form"
          onSubmit={handleContactSubmit}
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              id="name"
              type="text"
              placeholder="Your Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              placeholder="Your Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              rows="5"
              placeholder="Your Message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
            />
          </div>

          <AnimatePresence>
            {formStatus === 'success' && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                style={{ 
                  background: 'rgba(39, 174, 96, 0.1)', 
                  color: '#27AE60',
                  padding: '1rem',
                  borderRadius: '6px',
                  marginBottom: '1rem'
                }}
              >
                Thank you! Your message has been sent successfully.
              </motion.div>
            )}

            {formStatus === 'error' && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                style={{ 
                  background: 'rgba(235, 87, 87, 0.1)', 
                  color: '#EB5757',
                  padding: '1rem',
                  borderRadius: '6px',
                  marginBottom: '1rem'
                }}
              >
                There was an error sending your message. Please try again.
              </motion.div>
            )}
          </AnimatePresence>

          <motion.button
            type="submit"
            className="contact-button"
            whileHover={{ scale: 1.05, boxShadow: "0 10px 25px rgba(255, 87, 34, 0.3)" }}
            whileTap={{ scale: 0.95 }}
            disabled={formStatus === 'submitting'}
          >
            {isLoading ? (
              <div className="loading-spinner">
                <motion.div 
                  className="spinner"
                  animate={{ rotate: 360 }}
                  transition={{ 
                    duration: 1, 
                    repeat: Infinity, 
                    ease: "linear"
                  }}
                />
              </div>
            ) : formStatus === 'submitting' ? 'Sending...' : 'Request Demo'}
          </motion.button>
        </motion.form>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-logo">SafeLine Pickup</div>
        
        
        <p>© {new Date().getFullYear()} SafeLine Pickup. All rights reserved.</p>
      </footer>

      {/* "Back to Top" button */}
      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            className="back-to-top"
            onClick={scrollToTop}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            whileHover={{ y: -5 }}
          >
            ↑
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;
